/* eslint-disable max-classes-per-file */
import {
  Component,
  Directive,
  inject, Injectable, InjectionToken,
  Input, OnInit, Pipe, PipeTransform, Type,
} from '@angular/core';
import {
  ValidationErrors,
  FormControl, AbstractControl, ControlValueAccessor, NgControl,
} from '@angular/forms';
import { isDate } from 'lodash-es';
import moment from 'moment';
import {BsDatepickerInputDirective, DatepickerDateCustomClasses, BsDatepickerConfig} from 'webcommon/ap-ngx-bootstrap/datepicker';
import { DateUtilities } from 'webcommon/legacy-common';
import { BsDatepickerValue, GenericValidatorFn, injectParentForms, throwInvalidValue } from 'webcommon/shared';
import { _JsDateOnlyStringValue, _AnyServerDateType, ServerDateTime, ServerDateOnly, _DateTimeStringValue } from 'webcommon/shared/helpers-ng1520';
import {ApBaseCVAFormControl, ApChainCVA} from '../base-control';

// Navigate to the file for BsDatepickerValue
// for more notes on this alias
// todo: should i type ApDatePickerValue as | string, to allow Dto properties of string | Date?
export type ApDatePickerValue = BsDatepickerValue;
@Component({
  selector: 'ap-date-picker',
  styleUrls: [
    './ap-date-picker.component.less',
  ],
  templateUrl: './ap-date-picker.component.html',
})
export class ApDatePickerComponent
extends ApBaseCVAFormControl<FormControl<ApDatePickerValue>>
implements OnInit {
  private parentForms = injectParentForms();
  protected get disabled(): boolean {
    return this.innerControl ? this.innerControl.disabled : false;
  }

  @Input() baseId?: string;
  @Input() labelName: string;
  // Forward the host field name to the inner input for template-driven validators
  @Input() name?: string;
  @Input() showLabel = true;
  // * `BsDatepickerConfig` that can be used to override the default config.
  // * **Note**: This will override any custom date format
  @Input() customBSConfig?: Partial<BsDatepickerConfig>;
  @Input() showRequiredOnly = false;
  @Input() maxDate?: _AnyServerDateType;
  @Input() minDate?: _AnyServerDateType;

  @Input() formGroupSubmitted = false;

  // Default config that will be used for the date picker
  bsConfig: Partial<BsDatepickerConfig> = {
    adaptivePosition: true,
    containerClass: 'theme-emds-datepicker',
    dateInputFormat: 'M/D/YYYY',
    selectFromOtherMonth: true,
    showWeekNumbers: false,
  };

  error?: string; // String representation of the last `ValidationErrors`

  id: string;  // Id used to identify the child components
  dateCustomClasses?: DatepickerDateCustomClasses[];

  constructor(
  ) {
    super();
  }

  ngOnInit(): void {
    this.setId();
    if (this.customBSConfig) {
      this.bsConfig = this.customBSConfig;
    }

    const now = new Date();
    this.dateCustomClasses = [
      { date: now, classes: ['emds-datepicker-current-date-background-color'] },
    ];
  }

  isFormSubmitted(): boolean {
    for (const parentForm of this.parentForms) {
      if (parentForm.submitted || this.formGroupSubmitted) {
        return true;
      }
    }

    return false;
  }

  isRequired(): boolean {
    return !this.isShowRequiredOnly() && this.required;
  }

  isShowRequiredOnly(): boolean {
    return this.showRequiredOnly;
  }

  private isDate(value: any): boolean {
    return !isNaN(Date.parse(value));
  }

  // Sets the current `error` as a string representation of the `ValidationErrors`
  private setErrors(error: string): ValidationErrors {
    this.error = error;

    return { error };
  }

  private clearError(): null {
    this.error = undefined;

    return null;
  }

  private setId() {
    this.id = this.baseId + '-date-picker';
  }

  onApplyDatePickerStyle(): any {
    const obj: any = {};
    if (this.required || this.isShowRequiredOnly()) {
      obj.backgroundColor = '#fafad2';
    }
    return obj;
  }

  protected override onOuterControlChanged(outerControl: AbstractControl) {
    super.onOuterControlChanged(outerControl);
    outerControl.addValidators([this.validate]);
    // because the validators changed, we need to update the validity
    outerControl.updateValueAndValidity({emitEvent: false});
  }

  // TODO: move this comment to test cases
  /**
   * ## Validates the form control
   * ## Use cases:
   * ---
   * ### Case 0:
   *    Required: false
   *    Value: '' or null or undefined
   *
   *    Result: null
   * ---
   * ### Case 1:
   *    Required: false
   *    Value: valid date
   *
   *    Result: null
   * ---
   * ### Case 2:
   *    Required: false
   *    Value: invalid date
   *
   *    Result: error like 'date is invalid'
   * ---
   * ### Case 3:
   *    Required: true
   *    Value: '' or null or undefined
   *
   *    Result: error like 'required field'
   * ---
   * ### Case 4:
   *    Required: true
   *    Value: valid date
   *
   *    Result: null
   * ---
   * ### Case 5:
   *    Required: true
   *    Value: invalid date
   *
   *    Result: error like 'date is invalid'
   */
  validate: GenericValidatorFn<ApDatePickerValue> = (control) => {
    if (!control.value || !this.isDate(control.value)) {
      if (this.isShowRequiredOnly()) return this.clearError();
      else {
        if (!control.value) {
          return this.required ? this.setErrors(this.labelName + ' is a required field') : this.clearError();
        } else {
          return this.setErrors('Date is invalid');
        }
      }
    }

    return this.clearError();
  };

  validateCharacter(event: KeyboardEvent): KeyboardEvent | false {
    if (!isNaN(event.key as any)) {
      return event;
    } else if (event.key === '/' || event.key === '.') {
      return event;
    } else {
      return false;
    }
  }
}

const bsDatepickerCVA = new InjectionToken<Type<ControlValueAccessor>>('bsDatepickerCVA');

@Injectable()
export class ApDatePickerServerDateCVA extends ApChainCVA(BsDatepickerInputDirective)<
  ServerDateTime | null | undefined,
  ServerDateTime | undefined
>() {
  private config = inject(ApDatePickerServerDateDirective);

  override writeValue(value: ServerDateTime | null | undefined): void {
    this.baseCVA.writeValue(mapToBsDatePickerValue(value) ?? '');
  }

  override registerOnChange(fn: (value: ServerDateTime | undefined) => void): void {
    // todo: remove this type assertion after porting and fixing the types
    this.baseCVA.registerOnChange(((value: BsDatepickerValue) => {
      if (!value) {
        value = undefined;
      } else if (this.config.defaultToStartOfDay) {
        value = moment(value).startOf('day').toDate();
      } else if (this.config.defaultToEndOfDay) {
        value = moment(value).endOf('day').toDate();
      } else {
        value = moment(value).toDate();
      }
      fn(value);
    }) as () => void);
  }
}

// TODO: should i just make this the default everywhere, instead of opt-in??
// This directive allows for strings that would come from WebApi DateTime in JSON
// to be directly passed in for ngModel or formControl
// Normally, they would be typed as:
// ServerDateAndTime
// OR
// ServerDateAndTime | null
// OR
// AnyServerDateTime
// OR
// AnyServerDateTime | null
@Directive({
  selector: 'ap-date-picker[apServerDate]',
  providers: [
    {
      provide: bsDatepickerCVA,
      useValue: ApDatePickerServerDateCVA,
    },
  ],
})
export class ApDatePickerServerDateDirective {
  // Ignore the time component of the date when selecting, which means when selecting dates,
  // you'll always get a value that's at the start of the day.
  // This matters when filtering stuff based on dates on the server.
  // This is opt-in for now, but it feels like it should probably be usable in most cases,
  // since this component doesn't allow the user to pick a time, and the bsDatepicker defaults to the current time.
  @Input() defaultToStartOfDay = false;
  // This is the same as defaultTimeToStart, but for the end of the day,
  // like if you're picking the end of a date range or something
  @Input() defaultToEndOfDay = false;
}

@Injectable()
export class ApDatePickerDateOnlyCVA extends ApChainCVA(BsDatepickerInputDirective)<ServerDateOnly | null | undefined>() {
  private dateUtilities = inject(DateUtilities);

  override writeValue(value: ServerDateOnly | null | undefined): void {
    if ((value as unknown) instanceof Date) {
      // for DateOnly, we want to only use strings: for input and output
      // since formControl and ngModel isn't type-checked very well at the moment,
      // log an error at runtime for incorrect usages
      throwInvalidValue('ApDatePickerDateOnlyCVA.value', value);
    }

    this.baseCVA.writeValue(mapToBsDatePickerValue(value) ?? '');
  }

  override registerOnChange(fn: (value: ServerDateOnly | null | undefined) => void): void {
    // todo: remove this type assertion after porting and fixing the types
    this.baseCVA.registerOnChange(((value: BsDatepickerValue) => {
      let newValue: ServerDateOnly | null;
      if (!value) {
        newValue = null;
      } else {
        newValue = this.dateUtilities.getDateString(value);
      }
      fn(newValue);
    }) as () => void);
  }
}

// TODO: should i just make this the default everywhere, instead of opt-in??
// make ap-datepicker accept date strings and output date strings
// provides a CVA to be injected inside
@Directive({
  selector: 'ap-date-picker[apDateOnly]',
  providers: [
    {
      provide: bsDatepickerCVA,
      useValue: ApDatePickerDateOnlyCVA,
    },
  ],
})
export class ApDatePickerDateOnlyDirective {
}


// This directive enhances bsDatepicker to allow for defaulting to end of day or start of day
// when selecting a date, but it could grow to include any other value logic
@Directive({
  selector: 'input[bsDatepicker][apBsDatePicker]',
  providers: [
    // list of optional CVA options to override the default behavior
    ApDatePickerServerDateCVA,
    ApDatePickerDateOnlyCVA,
  ],
})
export class ApDatePickerInputDirective {
  constructor() {
    const cvaOverrideClass = inject(bsDatepickerCVA, {
      optional: true,
    });
    if (cvaOverrideClass) {
      const ngControlDirective = inject(NgControl, {
        self: true,
      });
      ngControlDirective.valueAccessor = inject(cvaOverrideClass);
    }
  }
}



@Pipe({
  name: 'fromServerDateString',
  pure: true,
})
export class FromServerDateStringPipe implements PipeTransform {
  // convert from Date | string to something that can fit BsDatepickerValue:
  transform = mapToBsDatePickerValue;
}


// Only need to do this extra mapping after getting the JSON object from the server,
// because the C# DateTime properties will come over from WebApi as ISO8601 string property in their plain parent object.
// Ideally, we could pass a reviver to the JSON parsing code in Angular to handle this conversion,
// but it's not currently supported. See this issue for details: https://github.com/angular/angular/issues/21079.
// ONLY Use this if trying to map with a 'source' of an ISO8601 string, or a falsy value like null.
// This assumes the datepicker expects local time
export function mapToBsDatePickerValue(
  source: _AnyServerDateType | null | undefined,
  defaultVal: BsDatepickerValue = undefined,
): BsDatepickerValue {
  if (!source) {
    return defaultVal;
  }

  if (isDate(source)) {
    return source;
  }

  if (isDateOnly(source)) {
    return dateOnlyStringToDate(source, false);
  }

  const date = new Date(source);
  return date;
}

const _timeComponentRegex = /\d{1,2}:\d{2}/;
export function isDateOnly(value: _DateTimeStringValue | ServerDateOnly): value is _JsDateOnlyStringValue {
  const hasTimeComponent = _timeComponentRegex.test(value);
  return !hasTimeComponent;
}

// see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#date_time_string_format
// When the time zone offset is absent, date-only forms are interpreted as a UTC time and date-time forms are interpreted as a local time.
// The interpretation as a UTC time is due to a historical spec error that was not consistent with ISO 8601
// but could not be changed due to web compatibility.
export function dateOnlyStringToDate(value: _JsDateOnlyStringValue, useUtc: boolean): Date {
  if (useUtc) {
    return new Date(value);
  }

  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d);
}
